/**
 * The ONE chart tooltip mechanism (wave 5 + its review, 2026-09-19), shared by every chart in
 * ./charts (`BarChart`, `StackedBarChart`, `DivergingBarChart`), Insights' `MiniBarChart` and the
 * Standard report's `CalendarHeatmap` (tabs/ReportsTab.tsx). Lives in its own module so ./charts
 * exports only components (react-refresh). See useChartTooltip below.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";

/** A committed chart tone → its solid fill class. Not status semantics — just a stable series colour. */
export type ChartTone = "brand" | "info" | "warn" | "ok";

export const TONE_DOT: Record<ChartTone, string> = {
  brand: "bg-(--brand)",
  info: "bg-(--info-solid)",
  warn: "bg-(--warn-solid)",
  ok: "bg-(--ok)",
};

/** What one tooltip says: the period (x label), the series and the formatted value. */
interface TipContent {
  period: string;
  series: string;
  value: string;
  tone?: ChartTone;
}

interface TipState extends TipContent {
  key: string;
  x: number;
  y: number;
}

/**
 * The one tooltip mechanism for every chart in ./charts (and Insights' `MiniBarChart`). `figureRef`
 * goes on the chart's `<figure>` (made `relative`); `target(key, content, at)` spreads onto each hoverable/focusable bar or segment, `at` = its grid position (col = bar index, row =
 * segment index, 0 = bottom); `bubble` is rendered inside the figure. Positions are figure-relative
 * pixels, so the bubble never reflows the chart.
 *
 * KEYBOARD (review 2026-09-19): a ROVING tabindex — ONE tab stop per chart (the first target, then the
 * last-focused one), not one per bar: a 92-day Standard report would otherwise put ~550 stops before
 * the Top users table. Inside the chart ArrowLeft/Right move between bars, ArrowUp/Down between the
 * segments of a stacked bar, Home/End jump to the first/last bar; focus shows the same tooltip.
 *
 * EDGES (review 2026-09-19): the bubble is centred on the pointer but CLAMPED to the figure after it is
 * measured, so on the first/last bar (or at phone width) it never pokes past the figure and never adds
 * horizontal page scroll; `max-w-full` + wrapping keeps it inside even when the text is wider.
 *
 * ROWS (wave 5 follow-up, 2026-09-19): `rowOrder` says which way a column's rows run on screen.
 * "bottom-up" (default) is a stacked bar — row 0 is the bottom segment, so ArrowUp goes to a HIGHER row.
 * "top-down" is a grid read downwards — the calendar heatmap (col = week, row = weekday, Sun at the
 * top): ArrowDown goes to the next day, ArrowUp to the previous one, and End jumps to the LAST cell
 * (the last day) instead of the last column's first row. Arrows never wrap in either order.
 */
export function useChartTooltip({
  rowOrder = "bottom-up",
}: { rowOrder?: "bottom-up" | "top-down" } = {}) {
  const figureRef = useRef<HTMLElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const tipId = useId();
  const [tip, setTip] = useState<TipState | null>(null);
  const [active, setActive] = useState<string | null>(null);
  // The keys `target()` hands out during THIS render, in order — the first is the default tab stop.
  const rendered: string[] = [];

  // A remembered stop whose bar is gone (new range / new data) falls back to the first target. Runs
  // after EVERY render on purpose: `rendered` is only complete once the component has rendered its
  // targets, so it cannot be a dependency; the guard makes it a no-op unless the stop really vanished.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- see above; converges in one extra render
  useEffect(() => {
    if (active != null && !rendered.includes(active)) setActive(null);
  });

  // Clamp the bubble horizontally inside the figure once its width is known (before paint).
  useLayoutEffect(() => {
    const b = bubbleRef.current;
    const f = figureRef.current;
    if (!tip || !b || !f) return;
    const fw = f.clientWidth;
    const half = b.offsetWidth / 2;
    if (fw <= 0 || half <= 0) return; // no layout (jsdom / hidden) — leave it at the pointer
    const left = half * 2 >= fw ? fw / 2 : Math.min(Math.max(tip.x, half), fw - half);
    b.style.left = `${left}px`;
  }, [tip]);

  const place = (
    key: string,
    content: TipContent,
    el: Element,
    clientX?: number,
    clientY?: number,
  ) => {
    const f = figureRef.current?.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const fl = f?.left ?? 0;
    const ft = f?.top ?? 0;
    const x = clientX != null ? clientX - fl : r.left + r.width / 2 - fl;
    const y = clientY != null ? clientY - ft : r.top - ft;
    setTip({ ...content, key, x, y });
  };

  /** Arrow/Home/End navigation between this chart's targets (found by their data-tip-* position). */
  const move = (e: KeyboardEvent<HTMLElement>, col: number, row: number) => {
    const all = Array.from(
      figureRef.current?.querySelectorAll<HTMLElement>("[data-tip-col]") ?? [],
    ).map((el) => ({ el, c: Number(el.dataset.tipCol), r: Number(el.dataset.tipRow) }));
    if (all.length === 0) return;
    const cols = [...new Set(all.map((t) => t.c))].sort((a, b) => a - b);
    const inCol = (c: number) => all.filter((t) => t.c === c).sort((a, b) => a.r - b.r);
    // Moving to another bar keeps the same segment row when that bar has it, else its nearest one.
    const nearest = (c: number) =>
      inCol(c).reduce((best, t) => (Math.abs(t.r - row) < Math.abs(best.r - row) ? t : best));
    const topDown = rowOrder === "top-down";
    let next: HTMLElement | undefined;
    if (e.key === "ArrowRight") {
      const c = cols.find((x) => x > col);
      if (c != null) next = nearest(c).el;
    } else if (e.key === "ArrowLeft") {
      const c = [...cols].reverse().find((x) => x < col);
      if (c != null) next = nearest(c).el;
    } else if (e.key === (topDown ? "ArrowDown" : "ArrowUp")) {
      next = inCol(col).find((t) => t.r > row)?.el;
    } else if (e.key === (topDown ? "ArrowUp" : "ArrowDown")) {
      next = [...inCol(col)].reverse().find((t) => t.r < row)?.el;
    } else if (e.key === "Home") {
      next = inCol(cols[0]!)[0]?.el;
    } else if (e.key === "End") {
      const last = inCol(cols[cols.length - 1]!);
      next = (topDown ? last[last.length - 1] : last[0])?.el;
    } else {
      return;
    }
    e.preventDefault();
    if (!next) return;
    // A chart that scrolls sideways (the heatmap at phone width) brings the target into view BEFORE
    // focusing it: the HTML focus() steps fire the focus event (→ onFocus, which measures the target to
    // place the bubble) before their own scroll-into-view, so the bubble would otherwise be placed at
    // the pre-scroll position. Not checked in a real browser. (jsdom has no scrollIntoView.)
    if (typeof next.scrollIntoView === "function") {
      next.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
    next.focus();
  };

  const target = (key: string, content: TipContent, at: { col: number; row?: number }) => {
    rendered.push(key);
    const row = at.row ?? 0;
    const isStop = active != null ? active === key : rendered.length === 1;
    return {
      tabIndex: isStop ? 0 : -1,
      role: "img" as const,
      "aria-label": `${content.period} · ${content.series}: ${content.value}`,
      "aria-describedby": tip?.key === key ? tipId : undefined,
      "data-tip-col": at.col,
      "data-tip-row": row,
      onPointerEnter: (e: PointerEvent<HTMLElement>) =>
        place(key, content, e.currentTarget, e.clientX, e.clientY),
      onPointerMove: (e: PointerEvent<HTMLElement>) =>
        place(key, content, e.currentTarget, e.clientX, e.clientY),
      onPointerLeave: () => setTip((cur) => (cur?.key === key ? null : cur)),
      onFocus: (e: FocusEvent<HTMLElement>) => {
        setActive(key);
        place(key, content, e.currentTarget);
      },
      onBlur: () => setTip((cur) => (cur?.key === key ? null : cur)),
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === "Escape") setTip(null);
        else move(e, at.col, row);
      },
    };
  };

  const bubble: ReactNode = tip ? (
    <span
      ref={bubbleRef}
      role="tooltip"
      id={tipId}
      style={{
        position: "absolute",
        left: tip.x,
        top: tip.y,
        zIndex: "var(--z-tooltip)",
        transform: "translate(-50%, calc(-100% - 0.5rem))",
      }}
      className={
        "pointer-events-none flex w-max max-w-full flex-col gap-0.5 rounded-(--radius-sm) " +
        "bg-(--text-strong) px-2.5 py-1.5 text-(length:--text-2xs) text-(--surface) " +
        "shadow-(--shadow-md) print:hidden"
      }
    >
      <span className="opacity-80">{tip.period}</span>
      <span className="tabular flex items-center gap-1.5 font-semibold">
        {tip.tone && (
          <span className={`size-2 shrink-0 rounded-[2px] ${TONE_DOT[tip.tone]}`} aria-hidden />
        )}
        {`${tip.series}: ${tip.value}`}
      </span>
    </span>
  ) : null;

  return { figureRef, target, bubble };
}

/** The shared focus ring for a chart target (a bar or a segment). */
export const TARGET_FOCUS =
  "outline-none focus-visible:relative focus-visible:z-10 focus-visible:outline-2 " +
  "focus-visible:outline-offset-1 focus-visible:outline-(--brand)";
